import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { DsButton, DsSpinner } from "../../../design-system";
import { assetService } from "../../../services/api";
import AssetAssignModal from "./AssetAssignModal";
import { OPERATION_SUB_TABS, fieldLabel, inputSm } from "./constants";

function asList(res, keys = []) {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  for (const k of keys) {
    if (Array.isArray(d?.[k])) return d[k];
    if (Array.isArray(res?.[k])) return res[k];
  }
  return [];
}

export default function AssetOperationsPanel({
  canAssign,
  sub: subProp,
  onSubChange,
}) {
  const [sub, setSub] = useState(subProp || "stock");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [assetId, setAssetId] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMode, setAssignMode] = useState("assign");
  const [assignAsset, setAssignAsset] = useState(null);
  const [maintNotes, setMaintNotes] = useState("");
  const [scrapReason, setScrapReason] = useState("");

  useEffect(() => {
    if (subProp && subProp !== sub) setSub(subProp);
  }, [subProp, sub]);

  const selectSub = (id) => {
    setSub(id);
    onSubChange?.(id);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (sub === "stock") res = await assetService.listStock();
      else if (sub === "verification") res = await assetService.listVerifications();
      else if (sub === "scrap") res = await assetService.listScrap();
      else if (sub === "recovery") res = await assetService.listRecoveries();
      else if (sub === "return" || sub === "assignment" || sub === "transfer" || sub === "maintenance") {
        res = await assetService.listAssets({
          status:
            sub === "return" || sub === "transfer" || sub === "assignment"
              ? "assigned"
              : sub === "maintenance"
                ? "under_maintenance"
                : undefined,
        });
      } else {
        res = { data: [] };
      }
      const keys =
        sub === "stock"
          ? ["stock", "assets", "rows"]
          : sub === "verification"
            ? ["verifications", "rows"]
            : sub === "scrap"
              ? ["scrap", "rows"]
              : sub === "recovery"
                ? ["recoveries", "rows"]
                : ["assets", "rows"];
      setRows(asList(res, keys));
    } catch (err) {
      setRows([]);
      toast.error(err?.response?.data?.message || "Failed to load operations");
    } finally {
      setLoading(false);
    }
  }, [sub]);

  useEffect(() => {
    load();
  }, [load]);

  const openAssign = (asset, mode) => {
    if (!canAssign) {
      toast.error("No permission for assignment actions");
      return;
    }
    setAssignAsset(asset);
    setAssignMode(mode);
    setAssignOpen(true);
  };

  const startMaint = async () => {
    if (!assetId) {
      toast.error("Enter asset ID");
      return;
    }
    try {
      await assetService.startMaintenance(assetId, { notes: maintNotes });
      toast.success("Maintenance started");
      setAssetId("");
      setMaintNotes("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed");
    }
  };

  const createScrap = async () => {
    if (!assetId) {
      toast.error("Enter asset ID");
      return;
    }
    try {
      await assetService.createScrap({ assetId, reason: scrapReason });
      toast.success("Scrap request created");
      setAssetId("");
      setScrapReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed");
    }
  };

  const recoveryAction = async (id, fn, msg) => {
    try {
      await fn(id);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">Operations</h1>
        <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
          Contextual stock, assignment, maintenance, scrap, and recovery actions.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-2">
        {OPERATION_SUB_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={
              sub === t.id
                ? "ds-page-nav-item ds-page-nav-item-active !w-auto"
                : "ds-page-nav-item !w-auto"
            }
            onClick={() => selectSub(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(sub === "assignment" || sub === "transfer" || sub === "return") && (
        <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
          Select an assigned asset below to {sub}. Prefer register row actions for day-to-day work.
        </p>
      )}

      {sub === "maintenance" ? (
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 space-y-2 max-w-md">
          <label className={fieldLabel}>Asset ID</label>
          <input className={inputSm} value={assetId} onChange={(e) => setAssetId(e.target.value)} />
          <label className={fieldLabel}>Notes</label>
          <input className={inputSm} value={maintNotes} onChange={(e) => setMaintNotes(e.target.value)} />
          <DsButton type="button" onClick={startMaint}>Start Maintenance</DsButton>
        </div>
      ) : null}

      {sub === "scrap" ? (
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 space-y-2 max-w-md">
          <label className={fieldLabel}>Asset ID</label>
          <input className={inputSm} value={assetId} onChange={(e) => setAssetId(e.target.value)} />
          <label className={fieldLabel}>Reason</label>
          <input className={inputSm} value={scrapReason} onChange={(e) => setScrapReason(e.target.value)} />
          <DsButton type="button" onClick={createScrap}>Create Scrap Request</DsButton>
        </div>
      ) : null}

      {sub === "verification" ? (
        <div className="flex gap-2">
          <DsButton
            type="button"
            onClick={async () => {
              try {
                await assetService.createVerification({ name: `Verification ${new Date().toISOString().slice(0, 10)}` });
                toast.success("Verification created");
                load();
              } catch (err) {
                toast.error(err?.response?.data?.message || "Failed");
              }
            }}
          >
            New Verification
          </DsButton>
        </div>
      ) : null}

      <div className="ds-page-panel overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
        {loading ? (
          <div className="flex justify-center py-12"><DsSpinner /></div>
        ) : rows.length === 0 ? (
          <p className="ds-caption p-6 text-[hsl(var(--ds-muted-fg))]">Nothing to show for this operation.</p>
        ) : (
          <ul className="divide-y divide-[hsl(var(--ds-border))]">
            {rows.map((row) => {
              const label =
                row.name ||
                row.tag ||
                row.assetTag ||
                row.assetName ||
                row.employeeName ||
                `#${row.id}`;
              return (
                <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <div>
                    <p className="ds-body font-medium text-[hsl(var(--ds-fg))]">{label}</p>
                    <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                      {row.status || row.condition || row.quantityAvailable != null
                        ? `Qty avail: ${row.quantityAvailable ?? row.quantity_available ?? "—"}`
                        : ""}
                      {row.amount != null ? ` · ₹${row.amount}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {sub === "assignment" ? (
                      <DsButton size="sm" onClick={() => openAssign(row, "assign")}>Assign / Reassign</DsButton>
                    ) : null}
                    {sub === "transfer" ? (
                      <DsButton size="sm" onClick={() => openAssign(row, "transfer")}>Transfer</DsButton>
                    ) : null}
                    {sub === "return" ? (
                      <DsButton size="sm" onClick={() => openAssign(row, "return")}>Return</DsButton>
                    ) : null}
                    {sub === "scrap" && row.status === "pending" ? (
                      <>
                        <DsButton size="sm" onClick={() => recoveryAction(row.id, assetService.approveScrap, "Approved")}>Approve</DsButton>
                        <DsButton size="sm" variant="outline" onClick={() => recoveryAction(row.id, assetService.rejectScrap, "Rejected")}>Reject</DsButton>
                        <DsButton size="sm" variant="danger" onClick={() => recoveryAction(row.id, assetService.disposeScrap, "Disposed")}>Dispose</DsButton>
                      </>
                    ) : null}
                    {sub === "recovery" ? (
                      <>
                        <DsButton size="sm" onClick={() => recoveryAction(row.id, assetService.approveRecovery, "Approved")}>Approve</DsButton>
                        <DsButton size="sm" variant="outline" onClick={() => recoveryAction(row.id, assetService.waiveRecovery, "Waived")}>Waive</DsButton>
                        <DsButton size="sm" variant="secondary" onClick={() => recoveryAction(row.id, assetService.sendRecoveryToFnf, "Sent to F&F")}>Send F&F</DsButton>
                      </>
                    ) : null}
                    {sub === "verification" && row.status !== "completed" ? (
                      <DsButton
                        size="sm"
                        onClick={() => recoveryAction(row.id, assetService.completeVerification, "Completed")}
                      >
                        Complete
                      </DsButton>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AssetAssignModal
        open={assignOpen}
        mode={assignMode}
        asset={assignAsset}
        onClose={() => setAssignOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
